import React, { useState, useEffect, useMemo } from 'react'
import { supabase } from './supabaseClient'
import { useAuth } from './AuthContext'
import { C } from './theme'
import { CopyButton } from './CopyButton'
import { PORTES_CUSTO_FALLBACK } from './portes'
import {
  TrendingUp, Download, RefreshCw, Home, Package,
  FileText, DollarSign, ShoppingCart, AlertTriangle,
} from 'lucide-react'

// Os portes que pagamos por unidade deixaram de ser um numero unico: sao do
// fornecedor desta factura (ver portesUnCusto). O fallback importado so entra
// quando a factura nao tem fornecedor definido. Ver src/portes.js.
const IVA = 0.23

// Paleta de cores pastel para distinguir encomendas (cor por encomenda, nao por linha)
const PALETA_ENCOMENDAS = [
  '#fffbe6', '#f0fdf4', '#eff6ff', '#fdf2f8', '#faf5ff',
  '#fff7ed', '#ecfeff', '#f7fee7', '#fef2f2', '#f5f5f4',
]
function corPorEncomenda(numero, mapa) {
  if (mapa.has(numero)) return mapa.get(numero)
  const cor = PALETA_ENCOMENDAS[mapa.size % PALETA_ENCOMENDAS.length]
  mapa.set(numero, cor)
  return cor
}

function fmt(n) {
  const v = Number(n || 0)
  return v.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('pt-PT')
}

function csvEscape(v) {
  const s = String(v ?? '')
  if (s.includes(';') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}

export default function AnaliseFactura({ compraInicial, onConsumido, onVoltar }) {
  const { isAdmin } = useAuth()

  const [compras, setCompras] = useState([])
  const [compraId, setCompraId] = useState(null)
  const [compraSel, setCompraSel] = useState(null)
  // Portes ao custo do fornecedor desta factura. Ver PORTES_UN, que era global.
  const [divergencia, setDivergencia] = useState(null)
  const [portesUnCusto, setPortesUnCusto] = useState(PORTES_CUSTO_FALLBACK)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingLista, setLoadingLista] = useState(true)
  const [errorMsg, setErrorMsg] = useState('')

  // Carregar lista de compras para dropdown
  useEffect(() => {
    let cancelado = false
    async function loadCompras() {
      setLoadingLista(true)
      const { data, error } = await supabase.from('compras_fornecedor')
        .select('id, numero_fatura, numero_fatura_final, fase, valor_total, valor_final_total, data_envio_prevista, pago_1a_prestacao, pago_2a_prestacao, created_at')
        .order('created_at', { ascending: false })
      if (cancelado) return
      if (error) {
        setErrorMsg('Compras: ' + error.message)
      } else {
        setCompras(data || [])
        if (!compraId) {
          // Se veio do Lucro Real a apontar para uma factura, abre-se nessa. E
          // o unico caso em que a escolha nao e nossa: alguem ja decidiu qual
          // quer ver, e abrir noutra qualquer obrigava-o a procura-la a mao —
          // que era exactamente o atrito que este caminho veio remover.
          const pedida = compraInicial && (data || []).some(c => c.id === compraInicial)
            ? compraInicial : null
          if (pedida) onConsumido?.()
          const primeiraPrevisao = (data || []).find(c => c.fase === 'previsao')
          setCompraId(pedida || primeiraPrevisao?.id || data?.[0]?.id || null)
        }
      }
      setLoadingLista(false)
    }
    loadCompras()
    return () => { cancelado = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Carregar detalhe da compra seleccionada
  useEffect(() => {
    if (!compraId) { setRows([]); setLoading(false); return }
    let cancelado = false
    async function loadDetalhe() {
      setLoading(true); setErrorMsg('')
      try {
        const [compraRes, linksRes, fornsRes] = await Promise.all([
          supabase.from('compras_fornecedor').select('*').eq('id', compraId).single(),
          supabase.from('compras_fornecedor_encomendas')
            .select('id, encomenda_id, produto_nome, custo_unitario_previsao, custo_unitario_final')
            .eq('compra_id', compraId),
          supabase.from('fornecedores').select('id, portes_custo_por_unidade'),
        ])
        if (cancelado) return
        if (compraRes.error) throw compraRes.error
        if (linksRes.error) throw linksRes.error
        const compra = compraRes.data
        setCompraSel(compra)
        // Portes que pagamos, por unidade, ao fornecedor DESTA factura. Era uma
        // constante de 0,75 € para toda a gente; num fornecedor sem portes isso
        // inventava transporte numa factura que nao o tem.
        const fornDesta = (fornsRes.data || []).find(f => f.id === compra?.fornecedor_id)
        setPortesUnCusto(fornDesta?.portes_custo_por_unidade != null
          ? Number(fornDesta.portes_custo_por_unidade)
          : PORTES_CUSTO_FALLBACK)
        const links = linksRes.data || []
        const encomendaIds = [...new Set(links.map(l => l.encomenda_id))]
        if (encomendaIds.length === 0) { setRows([]); setLoading(false); return }
        const [encRes, itensRes, perfisRes] = await Promise.all([
          supabase.from('encomendas')
            .select('id, numero_encomenda, cliente_id, cliente_email, cliente_nome, pago_1a_prestacao, pago_2a_prestacao, pago_1a_data, pago_2a_data, total_sem_iva, desconto_percentagem, desconto_valor, total_final, total_com_iva, pago_1a_valor, sinal_escolhido, tipo_pagamento')
            .in('id', encomendaIds),
          supabase.from('encomenda_itens')
            .select('id, encomenda_id, produto_nome, produto_ean, quantidade, preco_unitario, total_sem_iva')
            .in('encomenda_id', encomendaIds),
          supabase.from('perfis').select('id, cliente_interno').eq('cliente_interno', true),
        ])
        if (cancelado) return
        if (encRes.error) throw encRes.error
        if (itensRes.error) throw itensRes.error
        if (perfisRes.error) throw perfisRes.error
        const encMap = new Map((encRes.data || []).map(e => [e.id, e]))
        const internosIds = new Set((perfisRes.data || []).map(p => p.id))
        const linhas = []
        for (const link of links) {
          const enc = encMap.get(link.encomenda_id)
          if (!enc) continue
          const it = (itensRes.data || []).find(x =>
            x.encomenda_id === link.encomenda_id && x.produto_nome === link.produto_nome
          )
          const qty = Number(it?.quantidade || 0)
          const precoTabela = Number(it?.preco_unitario || 0)
          const totalTabela = Number(it?.total_sem_iva || 0)
          const descValor = Number(enc.desconto_valor || 0)
          const encTotalSemIva = Number(enc.total_sem_iva || 0)
          const ratio = (descValor > 0 && encTotalSemIva > 0)
            ? Math.max(0, (encTotalSemIva - descValor) / encTotalSemIva)
            : 1
          const totalVenda = totalTabela * ratio
          const precoVendaEfectivo = qty > 0 ? totalVenda / qty : 0
          const isInterno = internosIds.has(enc.cliente_id)
          const factorIva = isInterno ? 1 : (1 + IVA)
          const totalVendaCIva = totalVenda * factorIva
          const custoPrev = Number(link.custo_unitario_previsao || 0)
          const custoFin = Number(link.custo_unitario_final || 0)
          const custoUnit = compra.fase === 'final' && custoFin > 0 ? custoFin : custoPrev
          const totalCompra = qty * custoUnit
          const dataPago = enc.pago_2a_prestacao ? enc.pago_2a_data
            : (enc.pago_1a_prestacao ? enc.pago_1a_data : null)
          linhas.push({
            key: link.id || `${link.encomenda_id}_${link.produto_nome}`,
            numero_encomenda: enc.numero_encomenda,
            cliente_nome: enc.cliente_nome,
            cliente_email: enc.cliente_email,
            is_interno: isInterno,
            data_pago_completo: dataPago,
            pago_100: enc.pago_1a_prestacao && enc.pago_2a_prestacao,
            pago_50: enc.pago_1a_prestacao && !enc.pago_2a_prestacao,
            // Fraccao ja cobrada. Com sinal flexivel a 1.a pode valer 8%, nao 50%.
            frac_cobrada: (() => {
              const t = Number(enc.total_final ?? enc.total_com_iva ?? 0)
              if (!(t > 0)) return 0.5
              // A ordem importa: valor gravado primeiro, depois pagamento a
              // 100%, depois o sinal, e so no fim a metade. Faltava o degrau
              // do 100% — uma encomenda do Naypes, paga por inteiro a cabeca,
              // aparecia como tendo cobrado metade enquanto o valor nao fosse
              // gravado. E esta fraccao que reparte o custo pela receita ja
              // recebida, portanto o lucro saia errado para menos.
              const p1 = enc.pago_1a_valor != null ? Number(enc.pago_1a_valor)
                       : String(enc.tipo_pagamento) === '100' ? t
                       : enc.sinal_escolhido != null ? Number(enc.sinal_escolhido)
                       : t / 2
              return Math.min(1, p1 / t)
            })(),
            produto_nome: link.produto_nome,
            produto_ean: it?.produto_ean || '',
            quantidade: qty,
            preco_tabela: precoTabela,
            desc_pct: Number(enc.desconto_percentagem || 0),
            preco_venda_ef: precoVendaEfectivo,
            total_venda_semi_iva: totalVenda,
            total_venda_com_iva: totalVendaCIva,
            iva_venda: totalVendaCIva - totalVenda,
            custo_unit: custoUnit,
            total_compra: totalCompra,
            lucro_linha: totalVenda - totalCompra,
          })
        }
        // -- A FACTURA MANDA -----------------------------------------------
        // O custo por linha vinha das alocacoes, que sao calculadas com o
        // desconto gravado na ficha do fornecedor. Na factura 105 a Plus Skill
        // deu 12 por cento e a ficha diz 10: as alocacoes somavam 10.574,60
        // contra os 10.339,30 do papel, e o lucro aparecia 235 EUR abaixo do
        // real. O Lucro Real usava o valor da factura e dava certo — duas
        // contas do mesmo numero, e divergiram.
        //
        // Reparte-se a diferenca por todas as linhas em vez de a corrigir numa
        // so: as alocacoes sabem a PROPORCAO entre artigos, que e informacao
        // boa; o que nao sabem e o total, que esta escrito na factura. Com o
        // factor, o Lost Cherry passa de 62,82 a 61,42 — exactamente o que a
        // linha 7 da factura diz.
        //
        // Vale para qualquer desconto futuro sem ninguem lhe tocar: seja 10,
        // 12 ou 15, o total bate sempre com o papel.
        //
        // Em previsao normaliza-se ao valor da mercadoria (valor_sem_iva). Os
        // portes nao estao la porque so aparecem na factura final, ao envio —
        // e por isso que continuam a ser estimados por cima, em `totais`.
        const somaLinhas = linhas.reduce((sm, l) => sm + l.total_compra, 0)
        const mercadoriaFactura = Number(compra?.valor_sem_iva || 0)
        if (somaLinhas > 0 && mercadoriaFactura > 0) {
          const factor = mercadoriaFactura / somaLinhas
          setDivergencia({
            soma: Math.round(somaLinhas * 100) / 100,
            factura: Math.round(mercadoriaFactura * 100) / 100,
            pct: Math.round((factor - 1) * -10000) / 100,
          })
          // TECTO DE 10 POR CENTO, e nao por prudencia vaga.
          //
          // Isto reparte a diferenca por TODAS as linhas, o que assume que a
          // factura esta toda alocada a encomendas. Na 105 esta: 270 unidades
          // no papel, 270 alocadas. Mas se metade de uma factura for mercadoria
          // sem encomenda associada, a soma das linhas fica muito abaixo do
          // total e o factor atirava esse custo todo para cima dos artigos que
          // por acaso estao alocados — inventando um custo enorme em meia duzia
          // de linhas.
          //
          // Ate 10 por cento a diferenca e do genero da que aconteceu aqui: um
          // desconto, portes, arredondamentos. Acima disso ja nao e a mesma
          // coisa e nao se adivinha — mostra-se o aviso e deixa-se o numero
          // como esta, para quem olha perceber que ha ali qualquer coisa por
          // resolver em vez de levar com um custo corrigido a martelo.
          if (Math.abs(factor - 1) > 0.0001 && Math.abs(factor - 1) <= 0.10) {
            for (const l of linhas) {
              l.custo_unit = l.custo_unit * factor
              l.total_compra = Math.round(l.quantidade * l.custo_unit * 100) / 100
              l.lucro_linha = l.total_venda_semi_iva - l.total_compra
            }
          }
        } else {
          setDivergencia(null)
        }

        // ordenar por cliente + produto
        linhas.sort((a, b) => (a.cliente_nome || '').localeCompare(b.cliente_nome || '', 'pt') || (a.produto_nome || '').localeCompare(b.produto_nome || '', 'pt'))
        setRows(linhas)
      } catch (e) {
        if (!cancelado) setErrorMsg(e?.message || String(e))
      } finally {
        if (!cancelado) setLoading(false)
      }
    }
    loadDetalhe()
    return () => { cancelado = true }
  }, [compraId])

  const totais = useMemo(() => {
    // CLIENTES INTERNOS (Cátia) FORA DO LUCRO.
    // Ela compra ao custo — nunca há margem. Contar essas linhas como venda punha
    // dinheiro no denominador da margem sem pôr nada no numerador: quanto mais
    // comprasse para consumo próprio, pior parecia a margem do negócio. As outras
    // páginas (Lucro, Capital, Resumo, Top de Vendas) já as excluíam; esta não.
    //
    // As linhas continuam à vista, com o badge INT, porque a mercadoria dela veio
    // mesmo nesta factura e é precisa para conferir quantidades contra o fornecedor.
    let baseVenda = 0, ivaVenda = 0, totalVenda = 0
    let totalCompraLinhas = 0, totalQty = 0
    let jaPago = 0, faltaReceber = 0
    let vendaInterna = 0, custoInterno = 0, qtyInterna = 0

    for (const r of rows) {
      // Estes três incluem tudo — são o retrato da factura do fornecedor
      totalCompraLinhas += r.total_compra
      totalQty += r.quantidade

      if (r.is_interno) {
        vendaInterna += r.total_venda_semi_iva
        custoInterno += r.total_compra
        qtyInterna += r.quantidade
        continue   // não entra em venda, IVA, lucro nem contas a receber
      }

      baseVenda += r.total_venda_semi_iva
      ivaVenda += r.iva_venda
      totalVenda += r.total_venda_com_iva
      if (r.pago_100) jaPago += r.total_venda_com_iva
      else if (r.pago_50) {
        const f = r.frac_cobrada != null ? r.frac_cobrada : 0.5
        jaPago += r.total_venda_com_iva * f
        faltaReceber += r.total_venda_com_iva * (1 - f)
      }
      else faltaReceber += r.total_venda_com_iva
    }

    const isFinal = compraSel?.fase === 'final'
    const valorFinal = Number(compraSel?.valor_final_total || 0)
    const portesEstimados = totalQty * portesUnCusto
    // Portes reais = valor final da factura - subtotal das linhas (o resto é transporte)
    const portesReais = isFinal && valorFinal > 0 ? Math.max(0, valorFinal - totalCompraLinhas) : null
    const portesActivos = portesReais != null ? portesReais : portesEstimados
    // O que pagaste ao fornecedor por TUDO, incluindo a mercadoria da Cátia
    const totalCompraFornecedor = isFinal && valorFinal > 0 ? valorFinal : (totalCompraLinhas + portesActivos)

    // Parte da factura que corresponde ao consumo interno: o custo das linhas dela
    // mais a fatia de portes proporcional às unidades dela. É isto que se retira
    // ao custo antes de calcular o lucro, porque ela pagou exactamente esse valor.
    const portesInternos = totalQty > 0 ? portesActivos * (qtyInterna / totalQty) : 0
    const compraInterna = custoInterno + portesInternos
    const compraB2B = Math.max(0, totalCompraFornecedor - compraInterna)

    const lucroBruto = baseVenda - compraB2B
    const margemVenda = baseVenda > 0 ? (lucroBruto / baseVenda) * 100 : 0
    const markupCusto = compraB2B > 0 ? (lucroBruto / compraB2B) * 100 : 0
    return {
      baseVenda, ivaVenda, totalVenda, totalCompraLinhas, totalQty,
      portesEstimados, portesReais, portesActivos, totalCompraFornecedor,
      lucroBruto, margemVenda, markupCusto, isFinal, valorFinal,
      jaPago, faltaReceber,
      vendaInterna, compraInterna, qtyInterna, compraB2B, temInternos: qtyInterna > 0,
    }
  }, [rows, compraSel])

  const exportarCsv = () => {
    if (!compraSel) return
    const header = ['NEnc', 'Cliente', 'Interno', 'DataPago', 'Produto', 'EAN', 'Qtd', 'PrecoUnitEfectivo', 'TotalVendaSemIVA', 'TotalVendaComIVA', 'CustoUnit', 'TotalCompra', 'LucroLinha']
    const linhas = rows.map(r => [
      r.numero_encomenda, r.cliente_nome, r.is_interno ? 'sim' : 'nao',
      fmtDate(r.data_pago_completo), r.produto_nome, r.produto_ean, r.quantidade,
      r.preco_venda_ef.toFixed(2), r.total_venda_semi_iva.toFixed(2), r.total_venda_com_iva.toFixed(2),
      r.custo_unit.toFixed(2), r.total_compra.toFixed(2), r.lucro_linha.toFixed(2),
    ].map(csvEscape).join(';'))
    const totaisLinha = [
      '', 'TOTAIS', '', '', '', '', totais.totalQty,
      '', totais.baseVenda.toFixed(2), totais.totalVenda.toFixed(2),
      '', totais.totalCompraLinhas.toFixed(2), (totais.baseVenda - totais.totalCompraLinhas).toFixed(2),
    ].map(csvEscape).join(';')
    const portesLinha = [
      '', 'PORTES ' + (totais.isFinal ? '(FINAL)' : '(PREVISAO)'), '', '', '', '', '',
      '', '', '', '', totais.portesActivos.toFixed(2), '',
    ].map(csvEscape).join(';')
    const finalLinha = [
      '', 'LUCRO BRUTO FACTURA', '', '', '', '', '',
      '', totais.baseVenda.toFixed(2), '', '', totais.totalCompraFornecedor.toFixed(2), totais.lucroBruto.toFixed(2),
    ].map(csvEscape).join(';')
    const bom = '﻿'
    const conteudo = bom + [header.join(';'), ...linhas, totaisLinha, portesLinha, finalLinha].join('\n')
    const blob = new Blob([conteudo], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `analise-factura-${compraSel.numero_fatura || compraSel.id.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const recarregar = () => {
    if (!compraId) return
    // trigger useEffect
    const id = compraId
    setCompraId(null)
    setTimeout(() => setCompraId(id), 50)
  }

  if (!isAdmin) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
        <div style={{ textAlign: 'center', color: C.textMuted }}>
          <Package size={40} style={{ opacity: 0.4 }} />
          <p style={{ marginTop: '12px', fontSize: '14px' }}>Acesso restrito ao administrador.</p>
        </div>
      </div>
    )
  }

  const thBase = { padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', background: C.bg, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }
  const tdBase = { padding: '8px 10px', fontSize: '12px', color: C.text, borderBottom: `1px solid ${C.borderLight}`, verticalAlign: 'top' }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          {/* Sem entrada no menu, esta e a unica forma de sair daqui. */}
          {onVoltar && (
            <button onClick={onVoltar} title="Voltar ao Lucro"
              style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 3, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: C.textMuted }}>
              ← Lucro
            </button>
          )}
          <TrendingUp size={20} color={C.primary} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: C.text, margin: 0 }}>Análise Factura</h1>
        </div>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 18px' }}>
          Comparação venda ↔ compra por factura fornecedor. Ideal para saber o lucro bruto de cada operação em tempo real, mesmo antes de fechar a factura final.
        </p>

        {/* Selector + acções */}
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '14px', marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          <FileText size={14} color={C.textMuted} />
          <span style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Factura:</span>
          <select value={compraId || ''} onChange={e => setCompraId(e.target.value || null)}
            style={{ padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', background: '#fff', color: C.text, cursor: 'pointer', minWidth: '340px' }}>
            {loadingLista && <option>A carregar…</option>}
            {compras.map(c => {
              const label = `${c.numero_fatura || '(sem nº)'}${c.numero_fatura_final && c.numero_fatura_final !== c.numero_fatura ? ' → ' + c.numero_fatura_final : ''} · ${c.fase === 'final' ? '✅ Final' : '⏳ Previsão'} · ${c.data_envio_prevista ? fmtDate(c.data_envio_prevista) : 'sem data'}`
              return <option key={c.id} value={c.id}>{label}</option>
            })}
          </select>
          <div style={{ flex: 1 }} />
          <button onClick={recarregar} disabled={loading} style={{ padding: '7px 12px', border: `1px solid ${C.border}`, background: '#fff', borderRadius: '4px', cursor: loading ? 'wait' : 'pointer', fontSize: '12px', color: C.text, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <RefreshCw size={12} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} /> Recarregar
          </button>
          <button onClick={exportarCsv} disabled={rows.length === 0} style={{ padding: '7px 14px', border: 'none', background: C.primary, color: '#fff', borderRadius: '4px', cursor: rows.length === 0 ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '5px', opacity: rows.length === 0 ? 0.5 : 1 }}>
            <Download size={12} /> Exportar CSV
          </button>
        </div>

        {errorMsg && (
          <div style={{ padding: '12px', background: '#fef2f2', border: '1px solid #ef4444', borderRadius: '4px', color: '#b91c1c', fontSize: '13px', marginBottom: '10px' }}>Erro: {errorMsg}</div>
        )}

        {/* AS ALOCACOES NAO BATEM COM A FACTURA.
            Nao e erro — o custo mostrado ja foi ajustado ao papel. Mas uma
            diferenca grande quer dizer que o desconto gravado na ficha do
            fornecedor deixou de ser o que ele pratica, e isso afecta todos os
            artigos dele, nao so esta factura. Diz-se em vez de corrigir calado. */}
        {divergencia && Math.abs(divergencia.pct) >= 1 && (
          <div style={{ padding: '10px 14px', background: '#fff4ce', border: '1px solid #c0740055', borderRadius: '4px', color: '#92400e', fontSize: '12px', marginBottom: '10px', lineHeight: 1.5 }}>
            {Math.abs(divergencia.pct) <= 10 ? (
              <>
                <strong>Custos ajustados ao valor da factura.</strong> As alocações somavam {fmt(divergencia.soma)} € e a factura diz {fmt(divergencia.factura)} € — <strong>{divergencia.pct > 0 ? '−' : '+'}{fmt(Math.abs(divergencia.pct))}%</strong>.
                <br />Os números abaixo já usam o valor do papel. Mas confirma o desconto na ficha do fornecedor: se mudou, afecta o custo de todos os artigos dele.
              </>
            ) : (
              <>
                <strong>As alocações estão {fmt(Math.abs(divergencia.pct))}% ao lado da factura</strong> — somam {fmt(divergencia.soma)} € contra {fmt(divergencia.factura)} € do papel.
                <br />É demasiado para ser desconto ou portes. Provavelmente há mercadoria nesta factura que não está ligada a nenhuma encomenda. <strong>Os custos abaixo não foram ajustados</strong> — corrigir uma diferença desta dimensão espalhando-a pelas linhas daria números piores do que estes.
              </>
            )}
          </div>
        )}

        {/* Cabeçalho factura */}
        {compraSel && (
          <div style={{ background: compraSel.fase === 'final' ? '#e8f5e9' : '#fff4ce', border: `1px solid ${compraSel.fase === 'final' ? C.success : C.warning}55`, borderRadius: '6px', padding: '12px 16px', marginBottom: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', fontSize: '12px' }}>
            <div>
              <div style={{ fontSize: '10px', color: C.textMuted, textTransform: 'uppercase', fontWeight: '700' }}>Estado</div>
              <div style={{ fontWeight: '700', color: compraSel.fase === 'final' ? C.success : C.warning, marginTop: '2px' }}>
                {compraSel.fase === 'final' ? '✅ Final' : '⏳ Previsão'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: C.textMuted, textTransform: 'uppercase', fontWeight: '700' }}>Nº Factura</div>
              <div style={{ fontWeight: '700', color: C.text, marginTop: '2px', fontFamily: 'monospace' }}>{compraSel.numero_fatura || '—'}</div>
            </div>
            {compraSel.numero_fatura_final && compraSel.numero_fatura_final !== compraSel.numero_fatura && (
              <div>
                <div style={{ fontSize: '10px', color: C.textMuted, textTransform: 'uppercase', fontWeight: '700' }}>Nº Final</div>
                <div style={{ fontWeight: '700', color: C.text, marginTop: '2px', fontFamily: 'monospace' }}>{compraSel.numero_fatura_final}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize: '10px', color: C.textMuted, textTransform: 'uppercase', fontWeight: '700' }}>Envio Previsto</div>
              <div style={{ fontWeight: '700', color: C.text, marginTop: '2px' }}>{fmtDate(compraSel.data_envio_prevista)}</div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: C.textMuted, textTransform: 'uppercase', fontWeight: '700' }}>Encomendas</div>
              <div style={{ fontWeight: '700', color: C.text, marginTop: '2px' }}>{new Set(rows.map(r => r.numero_encomenda)).size}</div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: C.textMuted, textTransform: 'uppercase', fontWeight: '700' }}>Total Unidades</div>
              <div style={{ fontWeight: '700', color: C.text, marginTop: '2px' }}>{totais.totalQty}</div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>A carregar detalhe…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted, background: '#fff', border: `1px solid ${C.border}`, borderRadius: '6px' }}>
            Sem encomendas alocadas a esta factura.
          </div>
        ) : (
          <>
            {/* Grid unico: 2 colunas por linha (venda + compra) alinhadas + cor por encomenda */}
            {(() => {
              const corMap = new Map()
              const colGap = '14px'
              const cellBase = { padding: '10px 12px', fontSize: '12px', color: C.text, borderBottom: `1px solid ${C.border}`, minHeight: '58px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }
              const headerCellStyle = { padding: '10px 12px', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }
              return (
                <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden', marginBottom: '14px' }}>
                  {/* Cabecalhos das duas colunas */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: colGap, background: C.bg, borderBottom: `2px solid ${C.border}` }}>
                    <div style={{ padding: '10px 14px', background: '#e8f5e9', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <DollarSign size={14} color={C.success} />
                      <span style={{ fontSize: '12px', fontWeight: '700', color: C.success, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Venda aos clientes</span>
                    </div>
                    <div style={{ padding: '10px 14px', background: '#fde7e9', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ShoppingCart size={14} color={C.danger} />
                      <span style={{ fontSize: '12px', fontWeight: '700', color: C.danger, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Compra ao fornecedor · {totais.isFinal ? 'FINAL' : 'PREVISÃO'}</span>
                    </div>
                  </div>
                  {/* Headers de colunas */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: colGap, background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 60px 90px 100px' }}>
                      <div style={headerCellStyle}>Cliente · Nº Enc</div>
                      <div style={headerCellStyle}>Produto · EAN</div>
                      <div style={{ ...headerCellStyle, textAlign: 'right' }}>Qtd</div>
                      <div style={{ ...headerCellStyle, textAlign: 'right' }}>€/un ef.</div>
                      <div style={{ ...headerCellStyle, textAlign: 'right' }}>Total s/IVA</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 60px 90px 100px' }}>
                      <div style={headerCellStyle}>Cliente · Nº Enc</div>
                      <div style={headerCellStyle}>Produto · EAN</div>
                      <div style={{ ...headerCellStyle, textAlign: 'right' }}>Qtd</div>
                      <div style={{ ...headerCellStyle, textAlign: 'right' }}>Custo/un</div>
                      <div style={{ ...headerCellStyle, textAlign: 'right' }}>Total</div>
                    </div>
                  </div>
                  {/* Linhas: cada linha e um grid 2 colunas com venda + compra, ambos com mesma cor de fundo por encomenda */}
                  <div style={{ overflowX: 'auto' }}>
                    {rows.map(r => {
                      const cor = corPorEncomenda(r.numero_encomenda, corMap)
                      return (
                        <div key={r.key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: colGap, background: cor }}>
                          {/* VENDA */}
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 60px 90px 100px', minWidth: '480px' }}>
                            <div style={cellBase}>
                              <div style={{ fontWeight: '600' }}>
                                {r.cliente_nome}
                                {r.is_interno && <span title="Cliente interno" style={{ marginLeft: '4px', padding: '1px 5px', background: '#fef3c7', color: '#92400e', borderRadius: '3px', fontSize: '9px', fontWeight: '700' }}><Home size={8} style={{ verticalAlign: 'middle' }} /> INT</span>}
                              </div>
                              <div style={{ fontSize: '10px', color: C.textMuted, fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                {r.numero_encomenda}
                                {r.numero_encomenda && <CopyButton text={r.numero_encomenda} size={10} title="Copiar nº encomenda" />}
                              </div>
                              <div style={{ fontSize: '10px', color: r.pago_100 ? C.success : r.pago_50 ? C.warning : C.textMuted }}>
                                {r.pago_100 ? `✓ 100% · ${fmtDate(r.data_pago_completo)}` : r.pago_50 ? `50% · ${fmtDate(r.data_pago_completo)}` : 'não pago'}
                              </div>
                            </div>
                            <div style={cellBase}>
                              <div>{r.produto_nome}</div>
                              {r.produto_ean && <div style={{ fontSize: '10px', color: C.textMuted, fontFamily: 'monospace' }}>{r.produto_ean}</div>}
                            </div>
                            <div style={{ ...cellBase, textAlign: 'right', fontFamily: 'monospace', alignItems: 'flex-end' }}>{r.quantidade}</div>
                            <div style={{ ...cellBase, textAlign: 'right', fontFamily: 'monospace', alignItems: 'flex-end' }} title={r.desc_pct > 0 ? `Tabela: ${fmt(r.preco_tabela)}€ · Desc: -${r.desc_pct}%` : ''}>{fmt(r.preco_venda_ef)} €</div>
                            <div style={{ ...cellBase, textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', alignItems: 'flex-end' }}>{fmt(r.total_venda_semi_iva)} €</div>
                          </div>
                          {/* COMPRA */}
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 60px 90px 100px', minWidth: '480px' }}>
                            <div style={cellBase}>
                              <div style={{ fontWeight: '600' }}>{r.cliente_nome}</div>
                              <div style={{ fontSize: '10px', color: C.textMuted, fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                {r.numero_encomenda}
                                {r.numero_encomenda && <CopyButton text={r.numero_encomenda} size={10} title="Copiar nº encomenda" />}
                              </div>
                            </div>
                            <div style={cellBase}>
                              <div>{r.produto_nome}</div>
                              {r.produto_ean && <div style={{ fontSize: '10px', color: C.textMuted, fontFamily: 'monospace' }}>{r.produto_ean}</div>}
                            </div>
                            <div style={{ ...cellBase, textAlign: 'right', fontFamily: 'monospace', alignItems: 'flex-end' }}>{r.quantidade}</div>
                            <div style={{ ...cellBase, textAlign: 'right', fontFamily: 'monospace', alignItems: 'flex-end' }}>{fmt(r.custo_unit)} €</div>
                            <div style={{ ...cellBase, textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', alignItems: 'flex-end' }}>{fmt(r.total_compra)} €</div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {/* Rodapes lado a lado */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: colGap, borderTop: `2px solid ${C.border}` }}>
                    {/* Rodape venda */}
                    <div style={{ padding: '10px 14px', background: '#f9f9f9', borderTop: `2px solid ${C.success}`, display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px', fontSize: '12px', fontFamily: 'monospace' }}>
                      <span style={{ color: C.textMuted }}>Base tributável (s/IVA):</span>
                      <span style={{ textAlign: 'right', fontWeight: '700' }}>{fmt(totais.baseVenda)} €</span>
                      <span style={{ color: C.textMuted }}>IVA 23% (excl. interno):</span>
                      <span style={{ textAlign: 'right' }}>+{fmt(totais.ivaVenda)} €</span>
                      <span style={{ fontWeight: '700', borderTop: `1px solid ${C.border}`, paddingTop: '4px' }}>Total facturar aos clientes:</span>
                      <span style={{ textAlign: 'right', fontWeight: '700', color: C.success, borderTop: `1px solid ${C.border}`, paddingTop: '4px' }}>{fmt(totais.totalVenda)} €</span>
                      <span style={{ color: C.textMuted, fontSize: '10px' }}>· Já pago:</span>
                      <span style={{ textAlign: 'right', color: C.success, fontSize: '10px' }}>{fmt(totais.jaPago)} €</span>
                      <span style={{ color: C.textMuted, fontSize: '10px' }}>· Falta receber:</span>
                      <span style={{ textAlign: 'right', color: totais.faltaReceber > 0 ? C.warning : C.textMuted, fontSize: '10px' }}>{fmt(totais.faltaReceber)} €</span>
                    </div>
                    {/* Rodape compra */}
                    <div style={{ padding: '10px 14px', background: '#f9f9f9', borderTop: `2px solid ${C.danger}`, display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px', fontSize: '12px', fontFamily: 'monospace' }}>
                      <span style={{ color: C.textMuted }}>Subtotal linhas s/IVA:</span>
                      <span style={{ textAlign: 'right', fontWeight: '700' }}>{fmt(totais.totalCompraLinhas)} €</span>
                      {totais.isFinal ? (
                        <>
                          <span style={{ color: C.textMuted }}>Portes reais (fact. final):</span>
                          <span style={{ textAlign: 'right' }}>+{fmt(totais.portesReais)} €</span>
                        </>
                      ) : (
                        <>
                          <span style={{ color: C.textMuted }}>Portes estimados ({totais.totalQty} un × {fmt(portesUnCusto)}€):</span>
                          <span style={{ textAlign: 'right', color: C.warning }}>+{fmt(totais.portesEstimados)} €</span>
                        </>
                      )}
                      <span style={{ color: C.textMuted, fontSize: '10px' }}>IVA reverse charge OSS:</span>
                      <span style={{ textAlign: 'right', fontSize: '10px' }}>0.00 €</span>
                      <span style={{ fontWeight: '700', borderTop: `1px solid ${C.border}`, paddingTop: '4px' }}>Total factura fornecedor:</span>
                      <span style={{ textAlign: 'right', fontWeight: '700', color: C.danger, borderTop: `1px solid ${C.border}`, paddingTop: '4px' }}>{fmt(totais.totalCompraFornecedor)} €</span>
                      {!totais.isFinal && (
                        <span style={{ gridColumn: 'span 2', fontSize: '10px', color: '#92400e', fontStyle: 'italic', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <AlertTriangle size={11} /> Portes só definitivos após factura final. Valor pode ajustar.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* Rodapé lucro bruto */}
            <div style={{ background: totais.lucroBruto >= 0 ? 'linear-gradient(135deg, #dff6dd 0%, #b8e0b0 100%)' : 'linear-gradient(135deg, #fde7e9 0%, #f5b0b8 100%)', border: `2px solid ${totais.lucroBruto >= 0 ? C.success : C.danger}`, borderRadius: '8px', padding: '18px 22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <TrendingUp size={18} color={totais.lucroBruto >= 0 ? C.success : C.danger} />
                <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: totais.lucroBruto >= 0 ? '#0d4d10' : '#7a1a20' }}>Lucro Bruto desta Factura</h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', fontSize: '13px' }}>
                <div>
                  <div style={{ fontSize: '10px', color: '#5a5a5a', textTransform: 'uppercase', fontWeight: '700' }}>
                    Base venda s/IVA{totais.temInternos ? ' (só clientes)' : ''}
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: C.success, marginTop: '2px', fontFamily: 'monospace' }}>{fmt(totais.baseVenda)} €</div>
                </div>
                <div>
                  <div style={{ fontSize: '10px', color: '#5a5a5a', textTransform: 'uppercase', fontWeight: '700' }}>
                    Compra fornecedor{totais.temInternos ? ' (só clientes)' : ''}
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: C.danger, marginTop: '2px', fontFamily: 'monospace' }}>−{fmt(totais.compraB2B)} €</div>
                  {totais.temInternos && (
                    <div style={{ fontSize: '10px', color: '#5a5a5a', marginTop: '4px' }}>
                      factura total {fmt(totais.totalCompraFornecedor)} €
                    </div>
                  )}
                </div>
                <div style={{ borderLeft: `2px solid ${totais.lucroBruto >= 0 ? C.success : C.danger}`, paddingLeft: '14px' }}>
                  <div style={{ fontSize: '10px', color: '#5a5a5a', textTransform: 'uppercase', fontWeight: '700' }}>Lucro Bruto</div>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: totais.lucroBruto >= 0 ? '#0d4d10' : '#7a1a20', marginTop: '2px', fontFamily: 'monospace' }}>{totais.lucroBruto >= 0 ? '+' : ''}{fmt(totais.lucroBruto)} €</div>
                </div>
                <div>
                  <div style={{ fontSize: '10px', color: '#5a5a5a', textTransform: 'uppercase', fontWeight: '700' }}>Margem s/ venda</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: totais.lucroBruto >= 0 ? C.success : C.danger, marginTop: '2px' }}>{totais.margemVenda.toFixed(1)}%</div>
                  <div style={{ fontSize: '10px', color: '#5a5a5a', marginTop: '6px' }}>Markup s/ custo: <strong>{totais.markupCusto.toFixed(1)}%</strong></div>
                </div>
              </div>
              {totais.temInternos && (
                <div style={{ marginTop: '10px', padding: '8px 10px', background: 'rgba(255,255,255,0.65)', borderRadius: '4px', fontSize: '11px', color: '#5a5a5a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Home size={12} />
                  <span>
                    <strong>Consumo interno fora destas contas:</strong> {totais.qtyInterna} un ·
                    {' '}{fmt(totais.vendaInterna)} € a custo, que na factura do fornecedor valem {fmt(totais.compraInterna)} €.
                    Compra ao preço de custo, não gera margem — contá-la só diluía a margem real do negócio.
                  </span>
                </div>
              )}
              {!totais.isFinal && (
                <div style={{ marginTop: '10px', padding: '8px 10px', background: 'rgba(255,255,255,0.5)', borderRadius: '4px', fontSize: '11px', color: '#5a5a5a', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AlertTriangle size={12} /> Lucro calculado com portes estimados. Valor definitivo quando registares a factura final do fornecedor (fase 2).
                </div>
              )}
            </div>

            <p style={{ fontSize: '11px', color: C.textMuted, marginTop: '10px' }}>
              Linhas destacadas a amarelo são de <strong>clientes internos</strong> (IVA 0%, revenda a custo). Contam para o lucro se subires margem manualmente ou se comprares abaixo do custo interno.
              <br />
              IVA de venda vai para o Estado (não é lucro). IVA de compra é 0% (reverse charge OSS intracomunitário).
            </p>
          </>
        )}
      </div>
    </div>
  )
}
